import React, { useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  DialogActions,
  makeStyles,
} from "@material-ui/core";
import { Inputs, SelectComponet } from "../../Controls";
import LabeledField from "../../../Views/Home/InquiryRotationView/InquirySchemeManagement/components/LabeledField";
import LookupAutocomplete from "../../Controls/LookupAutocomplete/LookupAutocomplete";
import { StaticLookupsIds } from "../../../assets/json/StaticLookupsIds";
import ButtonsRadioSelector from "../../Controls/ButtonsRadioSelector/ButtonsRadioSelector";

import { ExpandMore } from "@material-ui/icons";
import SliderWithInputs from "./SliderWithInputs";
import PropertyAutocomplete from "./PropertyAutocomplete";
import UnitsAutocomplete from "./UnitsAutocomplete";
import DeveloperAutocomplete from "./DeveloperAutocomplete";
import { mapSeekerLeadDataToPostDto, mapUserDataToReferredBy } from "../models";
import { useCreateLeadContext } from "../context/CreateLeadContext";
import { useSelector } from "react-redux";
import { leadPost } from "../../../Services";
import { getErrorByName, showError, showSuccess } from "../../../Helper";
import { Spinner } from "../../SpinnerComponent/Spinner";
import { seekerLeadFormSchema } from "../validationSchemas/seekerLeadFormSchema";
import { useSuccessDialog } from "../../../SharedComponents";
import { CustomButton, CustomSelect, ReskinningInputs } from "../../V2";
import { useLayoutDetection } from "../../../Hooks";

const styles = makeStyles({
  addButton: {
    backgroundColor: "#7F6944",
    borderColor: "#7F6944",
    textTransform: "capitalize",
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#6b5c3a",
    },
  },
  cancelButton: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D0D5DD",
    textTransform: "capitalize",
    color: "#344054",
  },
  customSelect: {
    "& .MuiInput-root": {
      width: "100% !important",
      maxWidth: "512px",
    },
  },
});
const SeekerLeadForm = ({ onClose, contact }) => {
  const classes = styles();
  const { loginResponse } = useSelector((state) => state.login);
  const { initialStage, initialStatus, methodOfContact } =
    useCreateLeadContext();
  const { showDialog } = useSuccessDialog();
  const [loading, setLoading] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const referredBy = useMemo(
    () => mapUserDataToReferredBy(loginResponse),
    [loginResponse]
  );
  const { isNewLayout: isReskinningLayout, hasNewLayout } =
    useLayoutDetection();
  const isNewLayout = isReskinningLayout && hasNewLayout;
  const [lead, setLead] = React.useState({
    operationType: null,
    community: null,
    subCommunity: null,
    saleType: null,
    property: null,
    extraNotes: "",
    country: null,
    city: null,
    district: null,
    unit: null,
    budgetFrom: 10000,
    budgetTo: 60000000,
    sizeFrom: 200,
    sizeTo: 10000,
    views: [],
    fittingFixtures: [],
    unitUsageType: null,
    bathrooms: [{ name: "Any", id: "Any" }],
    bedrooms: [{ name: "Any", id: "Any" }],
    unitType: [],
  });
  const handleLeadChange = (field, value) => {
    switch (field) {
      case "country":
        setLead((prevLead) => ({
          ...prevLead,
          country: value,
          city: null,
          district: null,
          community: null,
          subCommunity: null,
          property: null,
        }));
        break;
      case "city":
        setLead((prevLead) => ({
          ...prevLead,
          city: value,
          district: null,
          community: null,
          subCommunity: null,
          property: null,
        }));
        break;
      case "district":
        setLead((prevLead) => ({
          ...prevLead,
          district: value,
          community: null,
          subCommunity: null,
          property: null,
        }));
        break;
      case "community":
        setLead((prevLead) => ({
          ...prevLead,
          community: value,
          subCommunity: null,
          property: null,
        }));
        break;
      default:
        setLead((prevLead) => ({
          ...prevLead,
          [field]: value,
        }));
        break;
    }
  };
  const schema = seekerLeadFormSchema.validate(lead, {
    abortEarly: false,
    allowUnknown: true,
  });
  const createLeadHandler = async () => {
    setLoading(true);
    setIsSubmitted(true);
    try {
      if (schema.error) {
        throw new Error("Please fill all required fields");
      }
      const body = mapSeekerLeadDataToPostDto({
        ...lead,
        status: initialStatus,
        leadStage: initialStage,
        methodOfContact,
        referredBy,
        contact,
      });
      const res = await leadPost(body);
      if ("leadId" in res) {
        showSuccess("Lead created successfully");
        showDialog("Lead created successfully", {
          name:
            contact?.contact?.company_name ??
            `${contact?.contact?.first_name} ${contact?.contact?.last_name}`,
          id: res.leadId,
        });
        onClose();
      } else {
        throw new Error("something went wrong, please try again later");
      }
    } catch (error) {
      showError(
        error.message ?? "something went wrong, please try again later"
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="inquiry-management-container p-3">
      <Spinner isActive={loading} isAbsolute />{" "}
      <LabeledField
        label={"Operation Type*"}
        component={
          <LookupAutocomplete
            lookupName={"LeadOperationType"}
            withError
            error={getErrorByName(schema, "operationType").error}
            helperText={getErrorByName(schema, "operationType").message}
            isSubmitted={isSubmitted}
            onChange={(_e, value) => handleLeadChange("operationType", value)}
            selectedValues={lead.operationType}
            isNewLayout={isNewLayout}
          />
        }
        hideDivider
      />
      <LabeledField
        label={"Unit Type*"}
        component={
          <LookupAutocomplete
            lookupName={"UnitType"}
            onChange={(_e, value) => handleLeadChange("unitType", value)}
            selectedValues={lead.unitType}
            multiple
            withError
            error={getErrorByName(schema, "unitType").error}
            helperText={getErrorByName(schema, "unitType").message}
            isSubmitted={isSubmitted}
            isNewLayout={isNewLayout}
          />
        }
      />
      <LabeledField
        label={"Country*"}
        component={
          <LookupAutocomplete
            lookupTypeId={StaticLookupsIds.Country}
            onChange={(_e, value) => handleLeadChange("country", value)}
            selectedValues={lead.country}
            withError
            error={getErrorByName(schema, "country").error}
            helperText={getErrorByName(schema, "country").message}
            isSubmitted={isSubmitted}
            isNewLayout={isNewLayout}
          />
        }
      />
      <LabeledField
        label={"City*"}
        component={
          <LookupAutocomplete
            lookupTypeId={StaticLookupsIds.Cities}
            onChange={(_e, value) => handleLeadChange("city", value)}
            selectedValues={lead.city}
            lookupParentId={lead.country?.lookupItemId}
            disabled={!lead.country}
            withError
            error={getErrorByName(schema, "city").error}
            helperText={getErrorByName(schema, "city").message}
            isSubmitted={isSubmitted}
            isNewLayout={isNewLayout}
          />
        }
      />
      <LabeledField
        label={"Bedrooms*"}
        component={
          <ButtonsRadioSelector
            options={[
              { name: "Any", id: "Any" },
              ...Array.from({ length: 8 }, (_, i) => ({
                id: i + 1,
                name: i + 1,
              })),
            ]}
            getSelected={(item, value) => value?.some((v) => v.id === item.id)}
            getLabel={(item) => item.name}
            onSelect={(item) => {
              if (item.at(-1).id === "Any" || item.length === 0) {
                handleLeadChange("bedrooms", [item.at(-1)]);
              } else if (
                item.at(-1).id !== "Any" &&
                item.some((v) => v.id !== "Any")
              ) {
                handleLeadChange(
                  "bedrooms",
                  item.filter((v) => v.id !== "Any")
                );
              } else {
                handleLeadChange("bedrooms", item);
              }
            }}
            multiple
            value={lead.bedrooms}
            getValue={(item) => item.id}
          />
        }
      />
      <LabeledField
        label={"Bathrooms*"}
        component={
          <ButtonsRadioSelector
            options={[
              { name: "Any", id: "Any" },
              ...Array.from({ length: 8 }, (_, i) => ({
                id: i + 1,
                name: i + 1,
              })),
            ]}
            getSelected={(item, value) => value?.some((v) => v.id === item.id)}
            getLabel={(item) => item.name}
            onSelect={(item) => {
              if (item.at(-1).id === "Any" || item.length === 0) {
                handleLeadChange("bathrooms", [item.at(-1)]);
              } else if (
                item.at(-1).id !== "Any" &&
                item.some((v) => v.id !== "Any")
              ) {
                handleLeadChange(
                  "bathrooms",
                  item.filter((v) => v.id !== "Any")
                );
              } else {
                handleLeadChange("bathrooms", item);
              }
            }}
            multiple
            value={lead.bathrooms}
            getValue={(item) => item.id}
          />
        }
      />
      <LabeledField
        label={"Extra notes"}
        component={
          isNewLayout ? (
            <ReskinningInputs
              inputPlaceholder={"Add notes or requirements"}
              multiline
              rows={4}
              value={lead.extraNotes}
              onInputChanged={(e) =>
                handleLeadChange("extraNotes", e.target.value)
              }
            />
          ) : (
            <Inputs
              multiline
              rows={4}
              inputPlaceholder={"Add notes or requirements"}
              value={lead.extraNotes}
              onInputChanged={(e) =>
                handleLeadChange("extraNotes", e.target.value)
              }
            />
          )
        }
      />
      <Accordion className="my-2">
        <AccordionSummary expandIcon={<ExpandMore />}>
          View more optional details
        </AccordionSummary>
        <AccordionDetails>
          <div className="d-flex-column">
            <LabeledField
              label={"Budget"}
              component={
                <SliderWithInputs
                  value={[lead.budgetFrom, lead.budgetTo]}
                  min={10000}
                  max={60000000}
                  onChange={(value) => {
                    handleLeadChange("budgetFrom", value[0]);
                    handleLeadChange("budgetTo", value[1]);
                  }}
                  getAriaValueText={(value) => `${value} AED`}
                  startAdornment={
                    <span className="fz-14px text-center px-1">AED</span>
                  }
                />
              }
            />
            <LabeledField
              label={"Size"}
              component={
                <SliderWithInputs
                  value={[lead.sizeFrom, lead.sizeTo]}
                  min={200}
                  max={10000}
                  onChange={(value) => {
                    handleLeadChange("sizeFrom", value[0]);
                    handleLeadChange("sizeTo", value[1]);
                  }}
                  getAriaValueText={(value) => `${value} Ft`}
                  startAdornment={
                    <span className="fz-14px text-center px-1">Ft</span>
                  }
                />
              }
            />
            <LabeledField
              label={"District"}
              component={
                <LookupAutocomplete
                  lookupTypeId={StaticLookupsIds.District}
                  onChange={(_e, value) => handleLeadChange("district", value)}
                  lookupParentId={lead.city?.lookupItemId}
                  selectedValues={lead.district}
                  disabled={!lead.city}
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"Community"}
              component={
                <LookupAutocomplete
                  lookupTypeId={StaticLookupsIds.Community}
                  onChange={(_e, value) => handleLeadChange("community", value)}
                  lookupParentId={lead.district?.lookupItemId}
                  selectedValues={lead.community}
                  disabled={!lead.district}
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"Sub community"}
              component={
                <LookupAutocomplete
                  lookupTypeId={StaticLookupsIds.SubCommunity}
                  onChange={(_e, value) =>
                    handleLeadChange("subCommunity", value)
                  }
                  selectedValues={lead.subCommunity}
                  lookupParentId={lead.community?.lookupItemId}
                  disabled={!lead.community}
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"Property"}
              component={
                <PropertyAutocomplete
                  value={lead.property}
                  onChange={(_e, value) => {
                    if (value) {
                      setLead({
                        ...lead,
                        property: value,
                        country: value.property.country,
                        city: value.property.city,
                        district: value.property.district,
                        community: value.property.community,
                        subCommunity: value.property.subCommunity,
                      });
                    } else {
                      setLead({
                        ...lead,
                        property: null,
                        country: null,
                        city: null,
                        district: null,
                        community: null,
                        subCommunity: null,
                      });
                    }
                  }}
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"Unit reference number"}
              component={
                <UnitsAutocomplete
                  value={lead.unit}
                  onChange={(_e, value) => handleLeadChange("unit", value)}
                  propertyId={lead.property?.propertyId}
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"Developer"}
              component={
                <DeveloperAutocomplete
                  value={lead.developer}
                  onChange={(_e, value) => handleLeadChange("developer", value)}
                  multiple
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"View"}
              component={
                <LookupAutocomplete
                  lookupName={"Views"}
                  onChange={(_e, value) => handleLeadChange("views", value)}
                  multiple
                  selectedValues={lead.views}
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"Fitting & fixtures"}
              component={
                <LookupAutocomplete
                  lookupName={"FittingAndFixtures"}
                  onChange={(_e, value) =>
                    handleLeadChange("fittingFixtures", value)
                  }
                  multiple
                  selectedValues={lead.fittingFixtures}
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"Unit usage type"}
              component={
                isNewLayout ? (
                  <Box className={classes.customSelect}>
                    <CustomSelect
                      options={["Residential", "Commercial"]}
                      defaultValue={lead.unitUsageType}
                      onSelectChanged={(value) =>
                        handleLeadChange("unitUsageType", value)
                      }
                      getOptionLabel={(option) => option}
                      getOptionValue={(option) => option}
                    />
                  </Box>
                ) : (
                  <SelectComponet
                    data={["Residential", "Commercial"]}
                    value={lead.unitUsageType}
                    onSelectChanged={(value) =>
                      handleLeadChange("unitUsageType", value)
                    }
                  />
                )
              }
            />
          </div>
        </AccordionDetails>
      </Accordion>
      <hr />
      <DialogActions>
        {isNewLayout ? (
          <>
            <CustomButton
              variant="outlined"
              onClick={onClose}
              color={"secondary"}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={createLeadHandler}
              disabled={loading}
            >
              Add seeker lead
            </CustomButton>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={onClose}
              className={classes.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className={classes.addButton}
              onClick={createLeadHandler}
              disabled={loading}
            >
              Add seeker lead
            </Button>
          </>
        )}
      </DialogActions>
    </div>
  );
};
export default SeekerLeadForm;
